import { useEffect, useRef } from 'react'
import { StyleSheet, View, Animated, Easing, Platform, type ViewStyle } from 'react-native'

interface Props {
  durationMs?: number
}

const startRotationAnimation = (durationMs: number, rotationDegree: Animated.Value): void => {
  Animated.loop(
    Animated.timing(rotationDegree, {
      toValue: 360,
      duration: durationMs,
      easing: Easing.linear,
      useNativeDriver: Platform.OS !== 'web',
    }),
  ).start()
}

export function LoadingSpinner({ durationMs = 2000 }: Props) {
  const rotationDegree = useRef(new Animated.Value(0)).current
  const innerRotationDegree = useRef(new Animated.Value(0)).current

  useEffect(() => {
    startRotationAnimation(durationMs, rotationDegree)
    startRotationAnimation(durationMs, innerRotationDegree)
  }, [durationMs, rotationDegree, innerRotationDegree])

  return (
    <View style={styles.container}>
      <View style={[styles.background, { borderColor: '#2D2D2D', opacity: 0.15 }]} />
      <Animated.View
        style={[
          styles.progress,
          { borderTopColor: '#2D2D2D' },
          {
            transform: [
              {
                rotateZ: rotationDegree.interpolate({
                  inputRange: [0, 360],
                  outputRange: ['0deg', '360deg'],
                }),
              },
            ],
          },
        ]}
      />
      <View style={styles.innerContainer}>
        <View style={[styles.innerBackground, { borderColor: '#D9D9D9', opacity: 0.25 }]} />
        <Animated.View
          style={[
            styles.innerCircle,
            { borderTopColor: '#D9D9D9' },
            {
              transform: [
                {
                  rotateZ: innerRotationDegree.interpolate({
                    inputRange: [0, 360],
                    outputRange: ['0deg', '360deg'],
                  }),
                },
              ],
            },
          ]}
        />
      </View>
    </View>
  )
}

const height = 162
const innerHeight = height / 2

function circle(height: number) {
  return {
    width: '100%',
    height: '100%',
    borderRadius: height / 2,
    borderWidth: 6,
  } satisfies ViewStyle
}

function quarterCircle(height: number) {
  return {
    ...circle(height),
    // just unbelievable that we can't use 'transparent' here...
    // https://github.com/facebook/react-native/issues/34722#issuecomment-1384229139
    borderLeftColor: 'rgba(0, 0, 0, 0.01)',
    borderRightColor: 'rgba(0, 0, 0, 0.01)',
    borderBottomColor: 'rgba(0, 0, 0, 0.01)',
    position: 'absolute',
  } satisfies ViewStyle
}

const styles = StyleSheet.create({
  container: {
    width: height,
    height,
    justifyContent: 'center',
    alignItems: 'center',
  },
  background: circle(height),
  progress: quarterCircle(height),
  innerContainer: {
    width: innerHeight,
    height: innerHeight,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
  },
  innerBackground: circle(innerHeight),
  innerCircle: quarterCircle(innerHeight),
})
